import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import DashboardHeader from './DashboardHeader';
import DashboardSidebar from './DashboardSidebar';
import StaffSidebar from './StaffSidebar';
import DashboardBody from './DashboardBody';
import StaffDashboard from './StaffDashboard';
import { useAuth } from '../../contexts/AuthContext';
import { useAttendanceBackgroundInit } from '../../services/attendanceBackgroundService';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.light.background.secondary};
  overflow-x: hidden;
  max-width: 100vw;
  width: 100%;
  box-sizing: border-box;
`;

const SidebarWrapper = styled.aside`
  width: 260px;
  flex-shrink: 0;
  background-color: ${theme.colors.dark.background.primary};
  border-right: 1px solid ${theme.colors.dark.border.light};
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  overflow-y: auto;
  z-index: 100;

  @media (max-width: ${theme.breakpoints.lg}) {
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    
    &.open {
      transform: translateX(0);
    }
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  height: 100vh;
  background-color: ${theme.colors.light.background.secondary};
  overflow: hidden;
  max-width: 100vw;
  box-sizing: border-box;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    max-width: 100vw;
  }
`;

const HeaderWrapper = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background-color: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.light.border.light};
  box-shadow: ${theme.shadows.sm};
`;

const BodyWrapper = styled.div`
  flex: 1;
  padding: ${theme.spacing[6]} ${theme.spacing[6]};
  background-color: ${theme.colors.light.background.secondary};
  overflow-y: auto;
  overflow-x: hidden;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;

  /* Custom scrollbar for better appearance */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.light.border.medium};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.primary[300]};
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    padding: 0 ${theme.spacing[4]};
  }
`;

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { companyData, staffData } = useAuth();

  // Initialize today's attendance in the background (non-blocking)
  useAttendanceBackgroundInit();

  // Determine if user is staff
  // If staffData exists, user is staff (companyData may exist for company logo/name)
  // If only companyData exists (no staffData), user is admin
  const isStaff = !!staffData;

  return (
    <LayoutContainer>
      <SidebarWrapper className={sidebarOpen ? 'open' : ''}>
        {isStaff ? (
          <StaffSidebar onClose={() => setSidebarOpen(false)} />
        ) : (
          <DashboardSidebar onClose={() => setSidebarOpen(false)} />
        )}
      </SidebarWrapper>
      <MainContent>
        <HeaderWrapper>
          <DashboardHeader onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        </HeaderWrapper>
        <BodyWrapper>
          {children || (isStaff ? <StaffDashboard /> : <DashboardBody />)}
        </BodyWrapper>
      </MainContent>
    </LayoutContainer>
  );
};

export default DashboardLayout;

